import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ChevronDown, MapPin, Filter, X, Bookmark, BookmarkCheck, Download, Activity } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import RealMap from '../components/RealMap';
import { api, API } from '../api';
import { useWatchlist } from '../context/WatchlistContext';

const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','GU','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','PR','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#4A7C5E]">
        <option value="">{placeholder}</option>
        {options.map((o, i) => <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]"/>
    </div>
  );
}

const ratingTone = (r) => {
  if (r >= 5) return 'bg-[#2D5F4F] text-white';
  if (r >= 4) return 'bg-[#4A7C5E] text-white';
  if (r >= 3) return 'bg-[#D4A85C] text-[#1A2622]';
  if (r >= 2) return 'bg-[#D58668] text-white';
  if (r >= 1) return 'bg-[#C75D5D] text-white';
  return 'bg-[#9A9077] text-white';
};

export default function FacilitiesPage() {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [state, setState] = useState(params.get('state') || '');
  const [minRating, setMinRating] = useState('');
  const [careMix, setCareMix] = useState(params.get('care_mix') || '');
  const [sort, setSort] = useState('rating');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [markers, setMarkers] = useState([]);
  const [showMap, setShowMap] = useState(true);
  const [heatmap, setHeatmap] = useState(false);
  const [heatmapData, setHeatmapData] = useState([]);
  const { isWatched, toggle } = useWatchlist();
  const LIMIT = 40;

  // Reset to page 0 when filters change
  useEffect(() => { setPage(0); }, [q, state, minRating, careMix, sort]);

  // Debounced fetch
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.facilities({ q: q || undefined, state: state || undefined, min_rating: minRating || undefined, care_mix: careMix || undefined, sort, limit: LIMIT, offset: page * LIMIT })
        .then((d) => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, state, minRating, careMix, sort, page]);

  useEffect(() => {
    api.markers({ state: state || undefined, limit: state ? 2000 : 800 }).then((d) => setMarkers(d.items || [])).catch(() => {});
  }, [state]);

  useEffect(() => {
    if (heatmap && heatmapData.length === 0) {
      api.maHeatmap().then((d) => setHeatmapData(d.items || [])).catch(() => {});
    }
  }, [heatmap, heatmapData.length]);

  const exportUrl = useMemo(() => api.exportFacilitiesCSV({
    ...(q ? { q } : {}),
    ...(state ? { state } : {}),
    ...(minRating ? { min_rating: minRating } : {}),
    limit: 5000,
  }), [q, state, minRating]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Facilities' }]} />

        <div className="mt-6 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <MapPin size={12} className="text-[#4A7C5E]"/>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">CMS Facilities</span>
            </div>
            <h1 className="mt-3 font-display text-4xl font-semibold text-[#1A2622] tracking-tight">Certified facilities</h1>
            <p className="mt-2 text-[14px] text-[#3A4A44]">Every Medicare/Medicaid-certified skilled nursing facility, sourced from CMS Provider Information.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={exportUrl} download onClick={() => { try { window.gtag && window.gtag('event', 'csv_export', { source: 'facilities', state: state || 'ALL' }); } catch (_) {} }} className="h-10 px-4 rounded-lg bg-[#1A2622] text-[#FBF6EC] text-[13px] hover:bg-[#2D5F4F] flex items-center gap-2" data-testid="export-csv-btn">
              <Download size={14}/> Export CSV
            </a>
            <button onClick={() => setHeatmap(!heatmap)} className={`h-10 px-4 rounded-lg text-[13px] flex items-center gap-2 border ${heatmap ? 'bg-[#4A7C5E] text-white border-[#4A7C5E]' : 'bg-white border-[#E5DCC4] text-[#1A2622] hover:border-[#4A7C5E]'}`} data-testid="heatmap-toggle">
              <Activity size={14}/> MA heatmap
            </button>
            <button onClick={() => setShowMap(!showMap)} className="h-10 px-4 rounded-lg bg-white border border-[#E5DCC4] text-[13px] text-[#1A2622] hover:border-[#4A7C5E] flex items-center gap-2">
              <MapPin size={14}/> {showMap ? 'Hide map' : 'Show map'}
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-5 relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A857F]"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Facility name or city…" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-10 pr-3 text-[13px] text-[#1A2622] placeholder:text-[#A8B0AA] outline-none focus:border-[#4A7C5E]"/>
          </div>
          <div className="md:col-span-2"><Select value={state} onChange={setState} options={US_STATES} placeholder="All states"/></div>
          <div className="md:col-span-2"><Select value={minRating} onChange={setMinRating} options={[{value:'5',label:'5 stars'},{value:'4',label:'4+ stars'},{value:'3',label:'3+ stars'},{value:'2',label:'2+ stars'}]} placeholder="Any rating"/></div>
          <div className="md:col-span-2">
            <Select testId="care-mix-filter" value={careMix} onChange={setCareMix} options={[
              {value:'skilled_rehab',  label:'Skilled Rehab Focused'},
              {value:'ltc_focused',    label:'Long-Term Care Focused'},
              {value:'medicare',       label:'Medicare Intensive'},
              {value:'medicaid',       label:'Medicaid Reliant'},
              {value:'private_pay',    label:'Private Pay Presence'},
              {value:'managed_care',   label:'Managed Care Oriented'},
              {value:'balanced',       label:'Balanced Care Mix'},
            ]} placeholder="Care Mix"/>
          </div>
          <div className="md:col-span-1"><Select value={sort} onChange={setSort} options={[{value:'rating',label:'Rating'},{value:'name',label:'Name'},{value:'occupancy',label:'Occ.'}]} placeholder="Sort"/></div>
        </div>

        <div className={`mt-6 grid grid-cols-1 ${showMap ? 'lg:grid-cols-12' : ''} gap-6`}>
          {showMap && (
            <div className="lg:col-span-7 lg:sticky lg:top-20 self-start">
              <RealMap markers={markers} height={620} heatmap={heatmap} heatmapData={heatmapData}/>
            </div>
          )}
          <div className={showMap ? 'lg:col-span-5' : ''}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[13px] text-[#3A4A44]">
                <span className="font-semibold text-[#1A2622]">{data.total.toLocaleString()}</span> facilities{state ? ` in ${state}` : ''}
                {data.total > 0 && <span className="text-[#7A857F] ml-2 text-[11.5px]">Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, data.total)}</span>}
              </div>
              {loading && <span className="text-[11px] text-[#7A857F]">loading…</span>}
            </div>
            <ul className={`space-y-2 ${showMap ? 'max-h-[560px] overflow-y-auto pr-2' : ''}`}>
              {data.items.map((f) => {
                const watched = isWatched('facility', f.slug);
                return (
                  <li key={f.ccn} className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-white border border-[#E5DCC4] hover:border-[#4A7C5E] transition-colors">
                    <Link to={`/facilities/${f.slug}`} className="flex items-center gap-3 min-w-0 flex-1">
                      <span className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-md text-sm font-semibold ${ratingTone(f.rating)}`}>{f.rating || '—'}</span>
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium text-[#1A2622] truncate">{f.name}</div>
                        <div className="text-[11px] text-[#7A857F] mt-0.5 truncate">
                          {f.city}{f.city && f.state ? ', ' : ''}{f.state}
                          <span className="mx-1 text-[#D4C9AF]">·</span>
                          <span className="font-mono text-[#5A6660]">CCN {f.ccn}</span>
                          {f.npi && (
                            <>
                              <span className="mx-1 text-[#D4C9AF]">·</span>
                              <span className="font-mono text-[#34538F]" title="NPPES Organization NPI" data-testid={`fac-npi-${f.ccn}`}>NPI {f.npi}</span>
                            </>
                          )}
                          {f.chain ? <> <span className="mx-1 text-[#D4C9AF]">·</span> {f.chain}</> : ''}
                        </div>
                      </div>
                    </Link>
                    <div className="shrink-0 flex items-center gap-2 text-[11px]">
                      {f.care_mix?.label && (
                        <span className="hidden md:inline px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap"
                              style={{ background: `${f.care_mix.color}1A`, color: f.care_mix.color }}
                              title={`${f.care_mix.label} · ${f.care_mix.reason} · confidence: ${f.care_mix.confidence}`}
                              data-testid={`care-mix-badge-${f.ccn}`}>
                          {f.care_mix.label === 'Skilled Rehab Focused' ? 'Skilled Rehab'
                            : f.care_mix.label === 'Long-Term Care Focused' ? 'LTC'
                            : f.care_mix.label === 'Strong Private Pay Presence' ? 'Private Pay'
                            : f.care_mix.label === 'Managed Care Oriented' ? 'Managed Care'
                            : f.care_mix.label === 'Medicare Intensive' ? 'Medicare'
                            : f.care_mix.label === 'Medicaid Reliant' ? 'Medicaid'
                            : 'Balanced'}
                        </span>
                      )}
                      {f.occupancy != null && <span className="text-[#3A4A44]"><b className="text-[#1A2622]">{f.occupancy}%</b></span>}
                      <button onClick={() => toggle({ kind: 'facility', slug: f.slug, name: f.name, city: f.city, state: f.state })} className={`p-1.5 rounded-md ${watched ? 'bg-[#4A7C5E] text-white' : 'text-[#7A857F] hover:bg-[#F5EFDD] hover:text-[#1A2622]'}`}>
                        {watched ? <BookmarkCheck size={13}/> : <Bookmark size={13}/>}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

            {data.total > LIMIT && (
              <div className="mt-5 flex items-center justify-between text-[13px] text-[#5A6660]">
                <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40 disabled:cursor-not-allowed">Previous</button>
                <span>Page {page + 1} of {totalPages.toLocaleString()}</span>
                <button onClick={() => setPage(page + 1)} disabled={page + 1 >= totalPages} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
